/*
  ft-cart.js — the ONE cart client.

  Why this file exists
  --------------------
  Before this, 29 files carried their own cart client (~85 calls to /cart/*), each with its own
  idea of what the cart was and its own way of patching the DOM. That produced: races between
  concurrent writes, a free gift that lagged behind the threshold, five competing
  "sync the cart icon" implementations, and an add-to-cart signal that was simultaneously
  inflated (system-injected lines firing ATC) and missing (plan upgrades via change.js firing
  nothing). Third parties — the Shopify Inbox agent, pixels, GTM — bound to whichever surface
  they found first.

  The rules this file enforces, and the reason for each:

  1. ONE writer. Every mutation goes through write(), and write() is serialised through a
     promise queue. Two concurrent cart writes cannot exist, so the cart can never be rendered
     from a response that is already stale.

  2. NO component fetches the cart. Components read FTCart.get() or listen for ft:cart:change.
     A component that fetches /cart.js is a second source of truth by definition.

  3. ONE render. write() asks Shopify for the drawer section and swaps only the nodes marked
     [data-ftc-region]. The shell — <ft-cart> and its scroll container — is never replaced, so
     scroll position survives and nothing needs re-binding.

  4. ALL interaction is delegated from the persistent root. No listener is ever attached to a
     node that a render can replace. This is what made the old drawer need a MutationObserver
     to repair itself.

  5. INTENT is carried on every event. A gift the system adds is not a customer adding to cart.
     The event contract below is what the pixel layer consumes, and it is the reason Meta and
     Google can be trusted again.

  Event contract (all bubble to document):
    ft:cart:change  { op, intent, cart, productLines, systemLines, previous }
        op           'add' | 'qty' | 'remove' | 'update' | 'plan' | 'refresh' | 'external'
        intent       'user'     — a customer action taken in this cart
                     'system'   — reconciliation we performed (gift add/remove)
                     'external' — a change something else made (an app, the Shopify chat,
                                  another tab, a return from checkout) that the store noticed
                                  and redrew. Shopify's own pixels already saw any add made on
                                  the page, so the tracking layer must never report it again.
        reason       external only: 'request' | 'read' | 'restore' | 'visible' | 'chat' |
                     'event' | 'manual'
        productLines lines that changed and are real merchandise (never gift/protection)
        systemLines  lines that changed and are system-injected
    ft:cart:open / ft:cart:close   { }
    ft:cart:error   { op, intent, message, source }
        source       the element an add came from, so the message shows beside it
    ft:cart:busy    { busy }
    ft:cart:items   { form, items, error }
        dispatched on a product form when it is submitted. A widget that owns the form sets
        `items` (or `error`); otherwise the store reads the form itself.
*/

(function () {
  'use strict';

  if (window.FTCart) return;

  var CFG = window.FTCartConfig || {};

  /* Product types that are injected by the store, not chosen by the customer.
     Using productType (not a line property) because it survives every code path that can
     create the line — PDP bundle add, threshold reconciliation, a manual admin order edit. */
  var SYSTEM_TYPES = (CFG.systemProductTypes || ['gift', 'Shipping Protection'])
    .map(function (t) { return String(t).toLowerCase(); });

  var SECTION = CFG.section || 'ft-cart-drawer';

  // ---------------------------------------------------------------- state

  /* The cart a render of the drawer section was drawn from. It ships once, as a JSON script
     outside the regions, so every render of the section carries it — the page load and the one
     an add returns alike — and commit() reads it from there instead of asking /cart.js again. */
  function cartFrom(doc) {
    var el = doc && doc.querySelector('script[data-ftc-cart]');
    if (!el) return null;
    try { return JSON.parse(el.textContent); } catch (err) { return null; }
  }

  var cart = cartFrom(document) || CFG.cart || null;   // last known cart JSON — the single source of truth
  var busy = false;
  var queue = Promise.resolve();    // the serialisation point
  var openState = false;

  function emit(name, detail) {
    document.dispatchEvent(new CustomEvent(name, { bubbles: true, detail: detail || {} }));
  }

  function setBusy(v) {
    if (busy === v) return;
    busy = v;
    document.documentElement.classList.toggle('ftc-busy', v);
    emit('ft:cart:busy', { busy: v });
  }

  function isSystemLine(line) {
    if (!line) return false;
    var t = String(line.product_type || '').toLowerCase();
    return SYSTEM_TYPES.indexOf(t) !== -1;
  }

  /* Shopify's line key is "variantId:propertiesHash" and is the ONLY safe way to address a
     line. Line indexes shift the moment any line is removed, which is how the old code
     occasionally changed the wrong row. */
  function keyOf(line) {
    return line && line.key;
  }

  function lineByKey(c, key) {
    if (!c || !c.items) return null;
    for (var i = 0; i < c.items.length; i++) if (c.items[i].key === key) return c.items[i];
    return null;
  }

  /* What changed between two cart snapshots, split by intent-relevance. The delta is what the
     pixel layer reports.

     Lines are matched by what makes them different TO THE CUSTOMER (variant, plan, properties),
     never by line.key. Shopify's key also changes when a line's discount changes: measured on the
     draft, raising a "3 Pack" discounted line from 12 to 13 gave it a new key, and the key-matched
     diff reported that +1 as "13 added, 12 removed". An automatic discount can also split one
     product across two lines. Summing quantity per identity is right in both cases. */
  function identity(l) {
    var p = (l.properties && !Array.isArray(l.properties)) ? l.properties : {};
    var props = Object.keys(p).sort().map(function (k) { return k + '=' + p[k]; }).join('&');
    var plan = l.selling_plan_allocation ? l.selling_plan_allocation.selling_plan.id : '';
    return l.variant_id + '|' + plan + '|' + props;
  }

  function diff(before, after) {
    var was = {}, now = {}, line = {};
    (before && before.items ? before.items : []).forEach(function (l) {
      var id = identity(l);
      was[id] = (was[id] || 0) + l.quantity;
      line[id] = l;
    });
    (after && after.items ? after.items : []).forEach(function (l) {
      var id = identity(l);
      now[id] = (now[id] || 0) + l.quantity;
      line[id] = l;   // report the line as it is now
    });

    var changed = [];
    Object.keys(line).forEach(function (id) {
      var a = was[id] || 0, b = now[id] || 0;
      if (a !== b) changed.push({ line: line[id], was: a, now: b, delta: b - a });
    });

    return {
      productLines: changed.filter(function (c) { return !isSystemLine(c.line); }),
      systemLines: changed.filter(function (c) { return isSystemLine(c.line); })
    };
  }

  // ---------------------------------------------------------------- render

  /* Replace only the marked regions. The shell survives, so:
       - the scroll container keeps its scrollTop
       - delegated listeners on the root stay valid
       - no MutationObserver is needed to notice or repair anything */
  function render(payload) {
    var html = payload && payload.sections && payload.sections[SECTION];
    if (!html) return null;

    var next = new DOMParser().parseFromString(html, 'text/html');
    var root = document.querySelector('ft-cart');
    if (!root) return cartFrom(next);

    var scroller = root.querySelector('[data-ftc-scroll]');
    var keepScroll = scroller ? scroller.scrollTop : 0;

    root.querySelectorAll('[data-ftc-region]').forEach(function (cur) {
      var name = cur.getAttribute('data-ftc-region');
      var incoming = next.querySelector('[data-ftc-region="' + name + '"]');
      if (!incoming) return;
      // innerHTML, not replaceWith: the region element itself is a stable anchor.
      cur.innerHTML = incoming.innerHTML;
      // carry state-bearing attributes (empty/full, tier reached) so CSS can react
      for (var i = 0; i < incoming.attributes.length; i++) {
        var a = incoming.attributes[i];
        if (a.name.indexOf('data-ftc-state') === 0) cur.setAttribute(a.name, a.value);
      }
    });

    if (scroller) scroller.scrollTop = keepScroll;
    // The count is NOT synced here — commit() does it once the authoritative cart has settled.
    // Syncing from a partial /cart/add.js payload is what put "1" on a four-item cart.
    return cartFrom(next);
  }

  /* The cart count badge. One implementation. The badge is only in the DOM when the cart has
     items — an empty badge hidden with [hidden] renders as a filled circle, because an
     author-level `display` beats the UA stylesheet's [hidden] rule. That was the phantom dot. */
  function syncCount() {
    var n = cart ? cart.item_count : 0;
    // Exclude system lines from the visible count: a free gift is not something the
    // customer put in the cart, and counting it made "1 item" read as "2".
    if (cart && cart.items) {
      n = cart.items.reduce(function (sum, l) { return sum + (isSystemLine(l) ? 0 : l.quantity); }, 0);
    }
    document.querySelectorAll('[data-ftc-count]').forEach(function (el) {
      el.textContent = n > 0 ? String(n) : '';
    });
    /* data-ftc-empty carries `display: none !important` in ft-cart.css, which outranks the
       `.cart-count-bubble { display: flex }` in critical.css. The `hidden` attribute does not,
       which is why an empty cart used to show a filled circle with no number in it. */
    document.querySelectorAll('[data-ftc-badge]').forEach(function (el) {
      el.toggleAttribute('data-ftc-empty', n === 0);
    });
    document.documentElement.classList.toggle('ftc-has-items', n > 0);
  }

  // ---------------------------------------------------------------- the one writer

  function enqueue(fn) {
    var run = queue.then(fn, fn);
    queue = run.then(function () {}, function () {});
    return run;
  }

  /* Take a cart-endpoint response, render it, settle the state, and announce it. ONE place,
     used by every writer including the site-wide add-to-cart interceptor.

     THE TRAP THIS EXISTS FOR: the cart endpoints do not return the same thing.

       /cart/change.js  and  /cart/update.js  return the FULL cart.
       /cart/add.js     returns ONLY the lines that were just added, plus `sections`.

     Treating an add response as a cart made `cart.items` the single added line, so the count
     badge read "1" on a four-item cart, the change diff was wrong (which would have corrupted
     the analytics intent), and reconcile() computed the qualifying subtotal from one line —
     meaning the free-gift threshold would have been evaluated against $39 instead of $103.

     The discriminator is the payload itself: a real cart has a numeric item_count. No flag to
     pass and nothing for a future caller to forget. */
  function commit(payload, before, op, intent) {
    // `sections` is rendered server-side from the CURRENT cart, so it is correct either way, and
    // it carries that cart as JSON: an add no longer waits on a second request to /cart.js
    // (0.4s measured) before the count and the gift reconciliation can run.
    var drawn = render(payload);

    var settled = (payload && typeof payload.item_count === 'number')
      ? Promise.resolve(payload)
      : drawn
        ? Promise.resolve(drawn)
        : fetch('/cart.js', { headers: { Accept: 'application/json' } }).then(function (r) { return r.json(); });

    return settled.then(function (c) {
      cart = c;
      syncCount();
      var d = diff(before, cart);
      emit('ft:cart:change', {
        op: op, intent: intent, cart: cart, previous: before,
        productLines: d.productLines, systemLines: d.systemLines
      });
      return cart;
    });
  }

  function write(url, body, opts) {
    opts = opts || {};
    var op = opts.op || 'update';
    var intent = opts.intent || 'user';

    return enqueue(function () {
      /* A body given as a function is decided HERE, inside the queue, against the cart as it stands
         once every earlier write has landed. reconcile() depends on it: deciding before the queue
         drained let two quick adds both see "no gift yet" and both add one. A decision of null
         means nothing to do: no request, no busy flash, no event. */
      if (typeof body === 'function') {
        body = body();
        if (!body) return Promise.resolve(cart);
      }
      setBusy(true);
      var before = cart;

      body.sections = SECTION;
      body.sections_url = window.location.pathname;

      markOwn();   // lets the outside-change watcher tell this request from anyone else's
      return fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(body)
      })
        .then(function (res) {
          return res.json().then(function (json) { return { ok: res.ok, json: json }; });
        })
        .then(function (r) {
          // Shopify signals cart errors with a `status` field on a 200, and with 4xx bodies.
          if (!r.ok || (r.json && r.json.status && r.json.status >= 400)) {
            var msg = (r.json && (r.json.description || r.json.message)) || 'Cart update failed.';
            throw new Error(msg);
          }
          return commit(r.json, before, op, intent);
        })
        .catch(function (err) {
          // `source` is the element the add came from: the message goes where the customer is
          // looking, not into a drawer that is not open.
          emit('ft:cart:error', { op: op, intent: intent, message: err.message || String(err), source: opts.source || null });
          /* Put the cart back to the truth, or an optimistic quantity and a busy row sit there
             after a refusal. resync(), NOT refresh(): this still runs INSIDE the queue, and
             refresh() enqueues, so it waited on this write while this write waited on it. That
             deadlock let one refused add hold the queue forever: the button kept spinning and
             every later add or quantity change on the page silently never ran until a reload. */
          return resync().then(function () { throw err; }, function () { throw err; });
        })
        .then(
          function (v) { setBusy(false); return v; },
          function (e) { setBusy(false); throw e; }
        );
    });
  }

  /* The server's cart and the rendered regions, straight from the server. NOT queued: its callers
     are either already inside the queue (a refused write) or refresh(), which queues it. */
  function resync() {
    return Promise.all([
      fetch('/cart.js', { headers: { Accept: 'application/json' } }).then(function (r) { return r.json(); }),
      fetch(window.location.pathname + '?sections=' + SECTION).then(function (r) { return r.json(); })
    ]).then(function (res) {
      render({ sections: res[1] });
      cart = res[0];
      syncCount();
      return cart;
    });
  }

  /* A read, not a write — but it still goes through the queue so it can never interleave with
     a pending mutation and overwrite newer state with older. Never call it from inside a queued
     task; see write(). */
  function refresh() {
    return enqueue(function () {
      return resync().catch(function () { return cart; });
    });
  }

  // ---------------------------------------------------------------- gift reconciliation

  /* The qualifying subtotal for reward tiers.

     Shipping protection is excluded because paying $2.89 to insure an order should not be what
     tips it over the free-shipping line. Gift lines are excluded because they are $0 and
     because counting a reward toward the next reward is circular. */
  function qualifyingSubtotal(c) {
    if (!c || !c.items) return 0;
    var skip = String(CFG.excludeProductType || '').toLowerCase();
    return c.items.reduce(function (sum, l) {
      var t = String(l.product_type || '').toLowerCase();
      if (t === skip || t === 'gift') return sum;
      return sum + l.final_line_price;
    }, 0);
  }

  /* The THRESHOLD gift line — and only that one.

     This used to return the first line of the gift VARIANT, which is wrong the moment a second
     offer hands out the same product. The subscription widget adds its own "Nose Strips Gift"
     line for subscribing (tagged _force_free_gift). Matching on variant alone meant:
       - over $110, reconcile saw the SUBSCRIPTION gift, decided the tier gift was already
         there, and never added it;
       - under $110, reconcile removed the first gift line it found — which could be the one the
         customer earned by subscribing, not the tier gift.
     Reconcile now only ever manages the line it created (_ft_gift: 'threshold'), plus lines the
     previous tier-gift snippet created (_ft_tier_gift), so it can never touch a gift that some
     other offer is responsible for. */
  function giftLine(c) {
    if (!c || !c.items || !CFG.giftVariantId) return null;
    for (var i = 0; i < c.items.length; i++) {
      var l = c.items[i];
      if (l.variant_id !== CFG.giftVariantId) continue;
      var p = l.properties;
      if (p && !Array.isArray(p) && (p._ft_gift === 'threshold' || p._ft_tier_gift)) return l;
    }
    return null;
  }

  /* Bring the gift line into agreement with the threshold.

     This is the bug that started the rebuild: the bar said "Free gift unlocked" while no gift
     was in the cart, and after dropping back below the threshold the gift lingered long enough
     to reach checkout. Both halves had the same cause — the reward was evaluated by a snippet
     that did not know when the cart had changed. It is now a step in the write path, so the
     cart cannot be rendered in a state where the bar and the lines disagree.

     Runs after a user action, after an outside change, and once at boot — never after its own
     system writes, which would recurse. Both halves are decided inside the queue (see write()). */
  function reconcile() {
    var auto = !!(CFG.giftAuto && CFG.giftVariantId && CFG.giftThreshold);

    /* 1. One of each gift, and the threshold gift only while the threshold is met. A gift line
          reached quantity 2 on the draft (14 Sep 2026): the subscription widget sends its gift with
          every add, so adding the same subscription twice merged two free gifts into one line. Gift
          lines show no stepper, so nothing on screen said so. */
    return write('/cart/update.js', function () {
      var updates = {};
      (cart && cart.items ? cart.items : []).forEach(function (l) {
        if (String(l.product_type || '').toLowerCase() === 'gift' && l.quantity > 1) updates[l.key] = 1;
      });
      var existing = auto ? giftLine(cart) : null;
      if (existing && qualifyingSubtotal(cart) < CFG.giftThreshold) updates[existing.key] = 0;
      return Object.keys(updates).length ? { updates: updates } : null;
    }, { op: 'update', intent: 'system' })
      // 2. The threshold gift, added only if it is still missing when this write's turn comes.
      .then(function () {
        if (!auto) return cart;
        return write('/cart/add.js', function () {
          if (giftLine(cart) || qualifyingSubtotal(cart) < CFG.giftThreshold) return null;
          return { items: [{ id: CFG.giftVariantId, quantity: 1, properties: { _ft_gift: 'threshold' } }] };
        }, { op: 'add', intent: 'system' });
      })
      // write() has already shown the error and re-synced. A failed correction must not make the
      // customer's own add, which succeeded, look like it failed.
      .catch(function () { return cart; });
  }

  // ---------------------------------------------------------------- public mutations

  /* Every user-intent mutation is followed by reconciliation. reconcile() is called after
     write() has RESOLVED, never from inside its then-chain: write holds the queue while it
     runs, so enqueueing from within it would wait on itself forever. */
  function afterUser(promise, intent) {
    if (intent === 'system') return promise;
    return promise.then(function () { return reconcile(); });
  }

  /* Opens the drawer on a user add by DEFAULT. Every call site wants that, and making it the
     default is what stops a new one forgetting — which is how the subscription widget ended up
     navigating to /cart. Pass { open: false } to suppress; the gift reconciliation runs as
     intent 'system' and never opens. */
  function add(items, opts) {
    opts = opts || {};
    var intent = opts.intent || 'user';
    var list = Array.isArray(items) ? items : [items];
    /* Open on the TAP, not on Shopify's answer. The add takes 0.8-1.3s server-side (measured on
       14 Sep 2026) and the drawer used to wait for that plus a second cart read, so a tap on the
       sticky bar looked like nothing happened for up to two seconds. The drawer opens now with
       the loading bar running, the new line renders into it when the answer lands, and a refused
       add shows its message in the open drawer. */
    if (intent === 'user' && opts.open !== false) open();
    return afterUser(write('/cart/add.js', { items: list }, { op: 'add', intent: intent, source: opts.source }), intent);
  }

  function setQty(key, qty, opts) {
    opts = opts || {};
    var intent = opts.intent || 'user';
    return afterUser(
      write('/cart/change.js', { id: key, quantity: Math.max(0, qty | 0) },
        { op: qty <= 0 ? 'remove' : 'qty', intent: intent }),
      intent
    );
  }

  function remove(key, opts) {
    return setQty(key, 0, opts);
  }

  /* Several lines at once, in ONE request. Removing a subscription that carries a linked gift
     has to be atomic — the old code did it as two requests and left a window where the gift
     was in the cart without its parent. */
  function update(map, opts) {
    opts = opts || {};
    var intent = opts.intent || 'user';
    return afterUser(
      write('/cart/update.js', { updates: map }, { op: opts.op || 'update', intent: intent }),
      intent
    );
  }

  /* Plan and quantity travel in ONE request. Sent separately, the line briefly existed as a
     subscription below its own minimum quantity, which the API then rejected.

     An EMPTY planId means "switch back to one-time", and that has to be sent as an explicit
     null. Omitting `selling_plan` from change.js means "leave it as it is", so the revert
     button would have appeared to do nothing at all.

     WHY IT STAMPS _ft_upgraded.
     "Switch back to one-time" should only exist for someone the CART upsold. A customer who
     chose a subscription on the product page picked it deliberately — offering them a way out
     of it in the cart is a downsell against our own conversion. So an upgrade performed here
     marks the line, and the revert link renders only on a line carrying that mark.

     change.js REPLACES a line's properties rather than merging, so the existing ones are
     copied forward or the min-quantity marker would be wiped along the way. */
  function setPlan(key, planId, qty, opts) {
    opts = opts || {};
    var intent = opts.intent || 'user';

    var line = lineByKey(cart, key);
    var props = {};
    var existing = (line && line.properties) || {};
    // Shopify returns [] rather than {} for a line with no properties.
    if (!Array.isArray(existing)) {
      Object.keys(existing).forEach(function (k) { props[k] = existing[k]; });
    }
    if (planId) props._ft_upgraded = 'cart';
    else delete props._ft_upgraded;

    var body = { id: key, quantity: qty | 0, selling_plan: planId ? planId : null, properties: props };
    return afterUser(write('/cart/change.js', body, { op: 'plan', intent: intent }), intent);
  }

  // ---------------------------------------------------------------- open / close

  function root() { return document.querySelector('ft-cart'); }

  /* On /cart the same <ft-cart> element IS the page (data-ftc-mode="page"). There is nothing to
     open or close there — the cart already is the page — so open and close do nothing, and in
     particular they never add the scroll lock, which would freeze an ordinary scrolling page. */
  function isPage() {
    var el = root();
    return !!el && el.getAttribute('data-ftc-mode') === 'page';
  }

  function open() {
    var el = root();
    if (!el) { window.location.href = '/cart'; return; }
    if (isPage()) return;
    openState = true;
    el.setAttribute('open', '');
    document.documentElement.classList.add('ftc-open');
    // focus the close button so keyboard and screen readers land inside the dialog
    var close = el.querySelector('[data-ftc="close"]');
    if (close) setTimeout(function () { close.focus(); }, 60);
    emit('ft:cart:open', {});
  }

  function close() {
    var el = root();
    if (!el || isPage()) return;
    openState = false;
    el.removeAttribute('open');
    document.documentElement.classList.remove('ftc-open');
    emit('ft:cart:close', {});
  }

  function toggle() { openState ? close() : open(); }

  // ---------------------------------------------------------------- delegation

  /* One click listener on document, for the life of the page. Every interactive element in the
     cart declares data-ftc="<action>"; nothing is ever bound directly, so a render can replace
     any node without breaking behaviour. */
  document.addEventListener('click', function (e) {
    var trigger = e.target.closest('[data-ftc]');
    if (!trigger) return;

    var action = trigger.getAttribute('data-ftc');
    var row = trigger.closest('[data-ftc-line]');
    // An offer card under the lines (the 3-month switch) names the line it changes with data-ftc-key.
    var key = row ? row.getAttribute('data-ftc-line') : trigger.getAttribute('data-ftc-key');

    switch (action) {
      case 'open':
        e.preventDefault();
        // Already on the cart page: the header icon takes you to the top of it instead.
        if (isPage()) { root().scrollIntoView({ behavior: 'smooth', block: 'start' }); break; }
        open();
        break;

      case 'close':
      case 'overlay':
        e.preventDefault();
        close();
        break;

      case 'plus':
      case 'minus': {
        e.preventDefault();
        if (!key || busy) return;
        var line = lineByKey(cart, key);
        if (!line) return;
        var min = parseInt(row.getAttribute('data-ftc-min') || '1', 10) || 1;
        var next = line.quantity + (action === 'plus' ? 1 : -1);
        if (action === 'minus' && next < min) return;        // subscription minimums
        optimisticQty(row, next);
        setQty(key, next);
        break;
      }

      case 'remove': {
        e.preventDefault();
        if (!key || busy) return;
        removeWithLinkedGifts(key, row);
        break;
      }

      case 'plan': {
        e.preventDefault();
        if (!key || busy) return;
        var planId = trigger.getAttribute('data-ftc-plan') || '';
        var qty = parseInt(trigger.getAttribute('data-ftc-qty') || '0', 10);
        var cur = lineByKey(cart, key);
        markRowBusy(row || document.querySelector('[data-ftc-line="' + key + '"]'), true);
        if (!row) trigger.setAttribute('aria-busy', 'true');
        setPlan(key, planId, qty || (cur ? cur.quantity : 1));
        break;
      }

      case 'add': {
        e.preventDefault();
        if (busy) return;

        var item;
        /* "Complete the kit" (ft-cart-kit) lists several variants: one pack of each in ONE
           request, with no line properties, so a pack already in the cart merges into its line. */
        var kit = (trigger.getAttribute('data-ftc-items') || '').split(',')
          .map(function (v) { return parseInt(v, 10); })
          .filter(function (v) { return v > 0; });
        if (kit.length) {
          item = kit.map(function (v) { return { id: v, quantity: 1 }; });
        } else {
          /* A multi-variant cross-sell card carries a <select>. Read it at the moment of the
             click rather than mirroring its value onto the button — one source of truth, and no
             change listener to keep in sync. */
          var card = trigger.closest('.ftc__card') || trigger.parentElement;
          var picker = card ? card.querySelector('[data-ftc-variant-select]') : null;
          var id = parseInt(
            (picker && picker.value) || trigger.getAttribute('data-ftc-variant') || '0', 10
          );
          if (!id) return;
          var q = parseInt(trigger.getAttribute('data-ftc-qty') || '1', 10) || 1;
          var plan = trigger.getAttribute('data-ftc-plan') || '';
          item = { id: id, quantity: q };
          if (plan) item.selling_plan = plan;
        }
        trigger.setAttribute('aria-busy', 'true');
        // add() opens on user intent by default — no open() call needed here.
        add(item)
          .catch(function () {})
          .then(function () { trigger.removeAttribute('aria-busy'); });
        break;
      }
    }
  });

  // ---------------------------------------------------------------- product-page adds

  /* EVERY add-to-cart that comes from a form goes through here, and this is the only code on a
     product page that decides what an add contains.

     WHY ONE OWNER. Four scripts on the product page each believed they owned Add to cart: this
     store, the theme's <product-form>, the subscription widget and the upsell block. Which one
     won depended on the template and on script order, and the losers failed two ways:
       - the upsell block's capture-phase hijack sent its own request and repainted a drawer that
         no longer exists, so the item was in the cart while the drawer and the count showed the
         cart from before the add. It only appeared on refresh.
       - the theme's <product-form>, finding no <cart-drawer>, sent the customer to /cart.
     The add is now described here and sent through add(), the same path as every other change:
     one request, one render, reconciliation, one ft:cart:change for the pixel layer, drawer open.

     WHAT AN ADD CONTAINS, in order of authority:
       1. the widget that owns the form, if one answers ft:cart:items. The subscription widget
          knows the pack, the plan minimum and the linked gift, which the fields do not.
       2. otherwise the quantity-break row, when the form's section has one for its product;
       3. otherwise the form's own fields: id, quantity, selling_plan, properties[...].
     Then the add-ons ticked in the same product section, so they travel in the SAME request as
     the product instead of as a second add. */

  function sectionOf(el) {
    return (el && el.closest && el.closest('.shopify-section')) || document;
  }

  function hasUpload(fd) {
    var found = false;
    fd.forEach(function (v) { if (typeof v !== 'string' && v && v.size > 0) found = true; });
    return found;
  }

  function propertiesOf(fd) {
    var props = {};
    fd.forEach(function (v, k) {
      if (typeof v !== 'string' || v === '' || k.indexOf('properties[') !== 0 || k.slice(-1) !== ']') return;
      props[k.slice(11, -1)] = v;
    });
    return props;
  }

  /* Add-ons ticked in the form's own product section. Stamped _ft_upsell so an add-on line can
     be told apart in the cart and in reporting; skipped when it is already one of the items. */
  function addonsFor(form, items) {
    var seen = {};
    items.forEach(function (it) { seen[it.id] = true; });
    var out = [];
    sectionOf(form).querySelectorAll(
      '.product-upsell-container[data-upsell-type="product-info"] .upsell-toggle-input:checked,' +
      '.product-upsell-container[data-upsell-type="product-page"] .upsell-toggle-input:checked'
    ).forEach(function (toggle) {
      var box = toggle.closest('.product-upsell-container');
      var id = parseInt((box && box.getAttribute('data-variant-id')) || toggle.getAttribute('data-variant-id') || '', 10);
      if (!id || seen[id]) return;
      seen[id] = true;
      out.push({ id: id, quantity: 1, properties: { _ft_upsell: 'true' } });
    });
    return out;
  }

  /* { items } or { error }. null when the form holds nothing this store can describe; the
     submit is then left to the browser, which lands on /cart, so it is still seen. */
  function describe(form) {
    var ask = { form: form, items: null, error: null };
    form.dispatchEvent(new CustomEvent('ft:cart:items', { bubbles: true, detail: ask }));
    if (ask.error) return { error: ask.error };

    var items = ask.items;
    if (!items || !items.length) {
      var fd = new FormData(form);
      var plan = parseInt(fd.get('selling_plan') || '', 10) || null;

      /* The quantity-break row keeps the chosen bundle in window.qbBundleData — one global for
         the whole page — so it is used only when this form's section holds the row for that
         same product, never for some other form that shares the page. */
      var row = sectionOf(form).querySelector('.qb-wrapper[data-product-id]');
      var qb = window.qbBundleData;

      if (row && qb && qb.items && qb.items.length && String(qb.productId) === row.getAttribute('data-product-id')) {
        items = qb.items.map(function (it) {
          var item = { id: parseInt(it.id, 10), quantity: parseInt(it.quantity, 10) || 1 };
          if (it.properties && Object.keys(it.properties).length) item.properties = it.properties;
          if (plan) item.selling_plan = plan;
          return item;
        });
      } else {
        var id = parseInt(fd.get('id') || '', 10);
        if (!id) return null;
        var one = { id: id, quantity: parseInt(fd.get('quantity') || '1', 10) || 1 };
        if (plan) one.selling_plan = plan;
        var props = propertiesOf(fd);
        if (Object.keys(props).length) one.properties = props;
        items = [one];
      }
    }

    return { items: items.concat(addonsFor(form, items)) };
  }

  /* ONE busy state for an add button, the theme's own: .loading swaps the label for its
     spinner, and aria-disabled turns a second tap into nothing instead of a second add. */
  function buttonBusy(btn, on) {
    if (!btn) return;
    btn.classList.toggle('loading', on);
    var spinner = btn.querySelector('.loading-overlay__spinner');
    if (spinner) spinner.classList.toggle('hidden', !on);
    btn.toggleAttribute('data-ftc-busy', on);   // ft-cart.css hides the label, shows the spinner
    if (on) btn.setAttribute('aria-disabled', 'true');
    else btn.removeAttribute('aria-disabled');
  }

  function addFromForm(e, form, btn) {
    // A tap while this button's add is still in flight.
    if (btn && btn.getAttribute('aria-disabled') === 'true') {
      e.preventDefault();
      e.stopImmediatePropagation();
      return;
    }

    // JSON cannot carry a file upload. The browser posts that form itself and lands on /cart.
    if (hasUpload(new FormData(form))) return;

    var described = describe(form);
    if (!described) return;

    /* Capture phase + stopImmediatePropagation: <product-form> binds submit on the form itself
       and the sticky bar binds its own click, so both would otherwise run as well. */
    e.preventDefault();
    e.stopImmediatePropagation();

    if (described.error) {
      emit('ft:cart:error', { op: 'add', intent: 'user', message: described.error, source: form });
      return;
    }

    /* The theme's "skip cart" setting goes straight to checkout, but only once the add has
       succeeded. Navigating on a failed add would lose the order. */
    var toCheckout = !!btn && btn.getAttribute('data-skip-cart') === 'true';

    buttonBusy(btn, true);
    add(described.items, { source: form, open: !toCheckout })
      .then(function () { if (toCheckout) window.location.href = '/checkout'; })
      .catch(function () { /* shown by the ft:cart:error listener */ })
      .then(function () { buttonBusy(btn, false); });
  }

  /* Path 1: a normal form submit. */
  document.addEventListener('submit', function (e) {
    var form = e.target;
    if (!form || !form.matches || !form.matches('form[action*="/cart/add"]')) return;

    /* A "Buy it now" style button posts straight to checkout; leave it alone. hasAttribute, NOT
       the formAction property: formAction always returns a URL (the form's own action when the
       attribute is absent), so the old truthiness test matched EVERY submit button. The store
       stood aside on every product form and <product-form> sent the customer to /cart. */
    var submitter = e.submitter;
    if (submitter && (submitter.name === 'checkout' || submitter.hasAttribute('formaction'))) return;

    addFromForm(e, form, submitter || form.querySelector('[type="submit"], [name="add"]'));
  }, true);

  /* Path 2: an add button OUTSIDE the form.

     The sticky add-to-cart is not inside the form. theme.liquid binds its click and calls
     productForm.handleSubmit(e) directly, and the quantity-break row swaps in a clone that
     dispatches a synthetic submit. Caught here in capture, ahead of both. */
  document.addEventListener('click', function (e) {
    var btn = e.target.closest('[data-ft-atc], [id^="SectionAtcBtn-"], .main-product-atc');
    if (!btn) return;
    if (btn.closest('form[action*="/cart/add"]')) return;   // in-form: path 1 owns it

    var form = document.querySelector('product-form[data-main="true"] form[action*="/cart/add"]')
      || document.querySelector('form[action*="/cart/add"]');
    if (!form) return;

    addFromForm(e, form, btn);
  }, true);

  /* Shipping protection is a checkbox, so it changes rather than clicks. It is a real customer
     choice but not merchandise, so it is tagged 'system' for the pixel layer — an insurance
     toggle is not an add-to-cart. */
  document.addEventListener('change', function (e) {
    var cb = e.target.closest('[data-ftc-protection]');
    if (!cb) return;

    var variant = parseInt(cb.getAttribute('data-ftc-variant') || '0', 10);
    /* data-ftc-protect-line, not data-ftc-line: the checkbox previously shared the line
       attribute with the item rows, so every `[data-ftc-line]` query picked it up as a
       phantom extra row. data-ftc-line now means "a cart line item" and nothing else. */
    var key = cb.getAttribute('data-ftc-protect-line') || '';

    if (cb.checked && variant) {
      add({ id: variant, quantity: 1 }, { intent: 'system' });
    } else if (!cb.checked && key) {
      setQty(key, 0, { intent: 'system' });
    }
  });

  document.addEventListener('keydown', function (e) {
    if (e.key === 'Escape' && openState) close();
  });

  /* ONE error surface. Previously an error could land in #CartDrawer-CartErrors, in a per-line
     #CartDrawer-LineItemError-N, or nowhere at all depending on which of the 29 cart clients
     had thrown — so a failed update often looked like nothing happening.

     An add from a product page reports in that page's own product-form error slot. The drawer
     opens only on success, so a message put only in the drawer would be a failure nobody sees:
     the same class of bug as a silent add. */
  function errorSlot(source) {
    if (!source || !source.closest) return null;
    var pf = source.closest('product-form') || sectionOf(source).querySelector('product-form');
    return pf ? pf.querySelector('.product-form__error-message-wrapper') : null;
  }

  document.addEventListener('ft:cart:error', function (e) {
    var msg = e.detail.message || 'Something went wrong. Please try again.';

    // The drawer opens on the tap, so while it is open the message belongs in the drawer, not in
    // the product form hidden behind it.
    var slot = openState ? null : errorSlot(e.detail.source);
    var text = slot && slot.querySelector('.product-form__error-message');
    if (text) {
      text.textContent = msg;
      slot.hidden = false;
      // Added from the sticky bar with the form scrolled away: bring the message into view.
      var r = slot.getBoundingClientRect();
      if (r.bottom < 0 || r.top > window.innerHeight) slot.scrollIntoView({ behavior: 'smooth', block: 'center' });
      return;
    }

    var box = document.querySelector('[data-ftc-error]');
    if (!box) return;
    box.textContent = msg;
    clearTimeout(box.__t);
    box.__t = setTimeout(function () { box.textContent = ''; }, 6000);
  });

  /* A successful change clears any stale error, so a message cannot outlive its cause. */
  document.addEventListener('ft:cart:change', function () {
    var box = document.querySelector('[data-ftc-error]');
    if (box) box.textContent = '';
    document.querySelectorAll('product-form .product-form__error-message-wrapper').forEach(function (slot) {
      slot.hidden = true;
      var text = slot.querySelector('.product-form__error-message');
      if (text) text.textContent = '';
    });
  });

  // ---------------------------------------------------------------- optimistic feedback

  /* Paint the new number immediately. The response is authoritative and will overwrite this
     ~300ms later; showing the old number until then is what made the cart feel broken. */
  function optimisticQty(row, next) {
    var out = row.querySelector('[data-ftc-qty-value]');
    if (out) out.textContent = String(next);
    markRowBusy(row, true);
  }

  /* ONE loading state for a row, used by quantity, remove and plan changes alike. The old cart
     had two different remove spinners that looked different depending on which code path ran. */
  function markRowBusy(row, v) {
    if (!row) return;
    row.toggleAttribute('data-ftc-row-busy', !!v);
    row.querySelectorAll('button, input, select').forEach(function (c) {
      c.disabled = !!v;
      if (v) c.setAttribute('aria-busy', 'true'); else c.removeAttribute('aria-busy');
    });
  }

  /* Removing a line that has gifts attached to it removes them in the SAME request. */
  function removeWithLinkedGifts(key, row) {
    var line = lineByKey(cart, key);
    if (!line) return;

    var updates = {};
    updates[key] = 0;

    if (!isSystemLine(line)) {
      var vid = String(line.variant_id);
      var plan = line.selling_plan_allocation ? String(line.selling_plan_allocation.selling_plan.id) : '';
      (cart.items || []).forEach(function (l) {
        if (!isSystemLine(l)) return;
        var p = l.properties || {};
        var parentVariant = String(p._ft_gift_parent_variant || p._force_free_gift_parent_variant || '');
        var parentPlan = String(p._ft_gift_parent_plan || p._force_free_gift_parent_plan || '');
        if (parentVariant && parentVariant === vid && parentPlan === plan) updates[l.key] = 0;
      });
    }

    markRowBusy(row, true);
    Object.keys(updates).forEach(function (k) {
      var r = document.querySelector('[data-ftc-line="' + k + '"]');
      if (r) markRowBusy(r, true);
    });

    update(updates, { op: 'remove' });
  }

  // ---------------------------------------------------------------- unlock toast

  /* Announce a reward the moment it is earned.

     Crossing a threshold is the one moment in a cart worth interrupting for, and the old cart
     did interrupt — but by inserting a banner into the flow, which shoved the item list down
     just as the customer was looking at it. This floats over the scroll area instead, so
     nothing moves.

     The tier comes from the progress region's own data attribute rather than being recomputed
     here, so the toast and the bar can never disagree about what was unlocked. */
  var lastTier = null;

  function showToast(tier) {
    var toast = document.querySelector('[data-ftc-toast]');
    var out = toast && toast.querySelector('[data-ftc-toast-text]');
    var tmpl = CFG.unlockToastText;
    if (!toast || !out || !tmpl) return;

    var bar = document.querySelector('[data-ftc-tier-labels]');
    var labels = bar ? (bar.getAttribute('data-ftc-tier-labels') || '').split('|') : [];
    var label = labels[tier - 1] || '';
    if (!label) return;

    out.textContent = tmpl.replace('[tier]', label);
    toast.setAttribute('data-show', '');
    clearTimeout(toast.__t);
    toast.__t = setTimeout(function () { toast.removeAttribute('data-show'); }, 3200);
  }

  document.addEventListener('ft:cart:change', function () {
    var bar = document.querySelector('[data-ftc-state-tier]');
    if (!bar) { lastTier = null; return; }
    var tier = parseInt(bar.getAttribute('data-ftc-state-tier') || '0', 10);
    // Only announce going UP, and never on the first render — landing on a page with a full
    // cart is not the moment the reward was earned.
    if (lastTier !== null && tier > lastTier) showToast(tier);
    lastTier = tier;
  });

  // ---------------------------------------------------------------- dispatch countdown

  /* The tick lives here, not in the snippet.

     Region renders use innerHTML, and a <script> inserted by innerHTML never executes — so a
     timer that shipped its own inline script would run once and then silently die the first
     time the customer changed a quantity. This loop re-queries the element every second, so a
     freshly rendered node is picked up with nothing to re-bind.

     One interval for the life of the page, and it does nothing while the element is absent. */
  function tickDispatch() {
    var el = document.querySelector('[data-ftc-dispatch]');
    if (!el || !window.FTDispatch) return;

    var out = el.querySelector('[data-ftc-dispatch-text]');
    if (!out) return;

    var info;
    try {
      info = window.FTDispatch.compute({
        cutoffHour: parseInt(el.getAttribute('data-cutoff') || '14', 10),
        tz: el.getAttribute('data-tz') || 'Australia/Brisbane'
      });
    } catch (err) { return; }

    if (!info || !info.clock) return;

    var tmpl = el.getAttribute('data-' + info.phase) || el.getAttribute('data-today') || '';
    if (!tmpl) return;

    // The clock is the only part that changes, so wrap just that in <b> and leave the rest text.
    out.innerHTML = tmpl
      .replace(/</g, '&lt;')
      .replace('[clock]', '<b>' + info.clock + '</b>');
  }

  setInterval(tickDispatch, 1000);
  document.addEventListener('ft:cart:change', tickDispatch);

  // ---------------------------------------------------------------- changes made by anything else

  /* The store redraws after its own writes. Other code changes the cart too, and until now the
     drawer and the count never noticed: tested 14 Sep 2026, an add from the Shopify chat reached
     the cart while the drawer kept showing the cart from before it, until a reload. Apps, Shop
     Pay's cart sync, a second tab and a return from checkout with the back button do the same.

     Letting those scripts into the drawer is not the fix — that is how the chat once injected the
     whole cart page into the old drawer. The store keeps the only render path and WATCHES for
     signs the cart changed, then re-reads it once:
       - a cart write that finished and was not ours, or another script reading /cart.json (the
         chat's runtime reads the cart from this page, so its requests are in this page's resource
         timing). Observed, never patched: fetch is left alone for Appstle and the tracking apps.
       - the page restored from the back/forward cache, or the tab or window regaining focus
       - the chat closing or receiving a message, and the Dawn-style cart events some apps fire
     A check is one small GET /cart.js; only a real difference fetches the sections and redraws.
     Normal browsing triggers nothing: the tracking app's attributes-only cart/update on every
     page load is recognised and skipped. */

  var CART_WRITE = /\/cart\/(add|change|update|clear)(?:\.js|\.json)?(?:\?|$)/;
  var CART_READ_BY_OTHERS = /\/cart\.json(?:\?|$)/;   // the store itself only ever reads /cart.js
  var ownStarts = [];
  var checkTimer = null;
  var lastCheck = Date.now();   // the server just rendered this page's cart

  function markOwn() {
    var now = performance.now();
    ownStarts = ownStarts.filter(function (t) { return now - t < 30000; });
    ownStarts.push(now);
  }

  function isOwn(entry) {
    return ownStarts.some(function (t) { return Math.abs(entry.startTime - t) < 200; });
  }

  // cart/update.js?attributes[...]=... changes cart attributes, never lines.
  function attributesOnly(url) {
    var q = url.split('?')[1];
    if (!q || url.indexOf('/cart/update') === -1) return false;
    return q.split('&').every(function (pair) {
      var k = decodeURIComponent(pair.split('=')[0] || '');
      return k.indexOf('attributes[') === 0 || k === 'note';
    });
  }

  function signature(c) {
    if (!c || !c.items) return '';
    return [c.token, c.item_count, c.total_price].concat(
      c.items.map(function (l) { return l.key + ':' + l.quantity; })
    ).join('|');
  }

  function checkNow(reason) {
    return enqueue(function () {
      var before = cart;
      lastCheck = Date.now();
      return fetch('/cart.js', { headers: { Accept: 'application/json' } })
        .then(function (r) { return r.json(); })
        .then(function (c) {
          if (signature(c) === signature(before)) return false;
          return fetch(window.location.pathname + '?sections=' + SECTION)
            .then(function (r) { return r.json(); })
            .then(function (sections) {
              render({ sections: sections });
              cart = c;
              syncCount();
              var d = diff(before, cart);
              emit('ft:cart:change', {
                op: 'external', intent: 'external', reason: reason, cart: cart, previous: before,
                productLines: d.productLines, systemLines: d.systemLines
              });
              return true;
            });
        })
        .catch(function () { return false; });
    })
      // Outside the queue, like every reconcile: an outside add can cross the free-gift line.
      .then(function (changed) { return changed ? reconcile() : cart; });
  }

  function scheduleCheck(reason, delay) {
    clearTimeout(checkTimer);
    checkTimer = setTimeout(function () { checkNow(reason); }, delay == null ? 300 : delay);
  }

  if (window.PerformanceObserver) {
    try {
      new PerformanceObserver(function (list) {
        list.getEntries().forEach(function (e) {
          if (CART_WRITE.test(e.name)) {
            if (!isOwn(e) && !attributesOnly(e.name)) scheduleCheck('request');
          } else if (CART_READ_BY_OTHERS.test(e.name) && Date.now() - lastCheck > 1500) {
            scheduleCheck('read', 400);
          }
        });
      }).observe({ type: 'resource', buffered: true });
    } catch (err) { /* no resource timing here: the signals below still apply */ }
  }

  window.addEventListener('pageshow', function (e) {
    if (e.persisted) scheduleCheck('restore', 0);
  });

  function onReturn() {
    if (document.visibilityState !== 'visible' || Date.now() - lastCheck < 2000) return;
    lastCheck = Date.now();
    scheduleCheck('visible', 0);
  }
  document.addEventListener('visibilitychange', onReturn);
  window.addEventListener('focus', onReturn);

  ['shopify-chat:close', 'shopify-chat:inbox-message-received'].forEach(function (name) {
    document.addEventListener(name, function () { scheduleCheck('chat', 600); });
  });

  ['cart:refresh', 'cart:updated', 'cart:change'].forEach(function (name) {
    document.addEventListener(name, function () { scheduleCheck('event'); });
  });

  // ---------------------------------------------------------------- boot

  /* Hydrate from the server once, so the store is never empty on first interaction, and so the
     count badge is correct even if the page was served from a cache with a stale header. */
  function boot() {
    syncCount();
    tickDispatch();
    /* Seed the tier from what the page already rendered. Left at null, the FIRST cart change
       of a session only records the tier instead of announcing it — so the very add that
       earned the reward would be the one add that never said so. */
    var bar = document.querySelector('[data-ftc-state-tier]');
    lastTier = bar ? parseInt(bar.getAttribute('data-ftc-state-tier') || '0', 10) : 0;
    if (!cart) refresh();
    // A cart can arrive already wrong, like a gift at quantity 2 from an earlier visit. Put it right
    // once. The decision runs inside the queue, so a correct cart costs no request.
    reconcile();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  window.FTCart = {
    get: function () { return cart; },
    isSystemLine: isSystemLine,
    add: add,
    setQty: setQty,
    remove: remove,
    update: update,
    setPlan: setPlan,
    refresh: refresh,
    check: function () { return checkNow('manual'); },
    open: open,
    close: close,
    toggle: toggle,
    get busy() { return busy; },
    get isOpen() { return openState; }
  };
})();
